/**
 * Design System - Button Component
 *
 * A versatile button component with multiple variants and sizes
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center gap-2 rounded-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-[#FDE047] text-[#1C1917] hover:bg-[#FDE047]/90 focus:ring-[#FDE047]/50',
        secondary:
          'bg-[#1C1917] text-white hover:bg-[#1C1917]/90 focus:ring-[#1C1917]/50',
        outline:
          'border border-[#D6D3D1] bg-white text-[#1C1917] hover:bg-[#F5F5F4] focus:ring-[#D6D3D1]',
        ghost:
          'bg-transparent text-[#1C1917] hover:bg-[#F5F5F4] focus:ring-[#D6D3D1]',
        link: 'bg-transparent text-[#1C1917] underline hover:text-[#57534E] focus:ring-0',
      },
      size: {
        sm: 'h-8 px-3 py-1.5 text-sm',
        md: 'h-10 px-4 py-2.5 text-base',
        lg: 'h-12 px-6 py-3 text-base',
        icon: 'h-10 w-10 p-2',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Content to be rendered inside the button
   */
  children?: React.ReactNode;
  /**
   * Icon to be rendered before the button text
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to be rendered after the button text
   */
  rightIcon?: React.ReactNode;
  /**
   * If true, the button will show a loading state
   */
  isLoading?: boolean;
  /**
   * Custom class name
   */
  className?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      children,
      leftIcon,
      rightIcon,
      isLoading,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <svg
            className='animate-spin h-4 w-4'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            ></circle>
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            ></path>
          </svg>
        ) : (
          <>
            {leftIcon && (
              <span className='inline-flex shrink-0'>{leftIcon}</span>
            )}
            {children}
            {rightIcon && (
              <span className='inline-flex shrink-0'>{rightIcon}</span>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
